import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ModalConfig, ModalResult } from './dynamic-modal.models';

/**
 * DynamicModalService
 *
 * Abre modais programaticamente e retorna o resultado como Observable.
 *
 * @example
 * // Confirmação de exclusão
 * this.modal.confirm('Excluir registro', 'Tem certeza? Esta ação não pode ser desfeita.')
 *   .subscribe(result => { if (result.action === 'confirm') this.delete(); });
 *
 * // Sucesso
 * this.modal.success('Registro salvo', 'O cadastro foi atualizado com sucesso.');
 *
 * // Formulário
 * this.modal.form('Novo item', [{ key: 'name', label: 'Nome', type: 'text', required: true }])
 *   .subscribe(result => { if (result.action === 'confirm') console.log(result.formValues); });
 */
@Injectable({ providedIn: 'root' })
export class DynamicModalService {
  private configSubject = new Subject<ModalConfig | null>();
  private resultSubject = new Subject<ModalResult>();

  config$ = this.configSubject.asObservable();

  // ─── Métodos de conveniência ────────────────────────────────────────────────

  confirm(title: string, message: string, config?: Partial<ModalConfig>): Observable<ModalResult> {
    return this.open({ type: 'confirm', title, message, ...config });
  }

  success(title: string, message?: string): Observable<ModalResult> {
    return this.open({ type: 'success', title, message, buttons: [{ label: 'Ok', variant: 'success', icon: 'bx-check', action: 'confirm' }] });
  }

  error(title: string, message?: string): Observable<ModalResult> {
    return this.open({ type: 'error', title, message, buttons: [{ label: 'Fechar', variant: 'danger', action: 'cancel' }] });
  }

  warning(title: string, message?: string): Observable<ModalResult> {
    return this.open({ type: 'warning', title, message, buttons: [{ label: 'Entendi', variant: 'warning', action: 'confirm' }] });
  }

  info(title: string, message?: string): Observable<ModalResult> {
    return this.open({ type: 'info', title, message, buttons: [{ label: 'Ok', variant: 'primary', action: 'confirm' }] });
  }

  form(title: string, fields: ModalConfig['formFields'], config?: Partial<ModalConfig>): Observable<ModalResult> {
    return this.open({ type: 'form', title, formFields: fields, size: 'md', ...config });
  }

  // ─── Core ───────────────────────────────────────────────────────────────────

  open(config: ModalConfig): Observable<ModalResult> {
    this.configSubject.next(config);
    return this.resultSubject.asObservable().pipe(take(1));
  }

  close(result: ModalResult): void {
    this.resultSubject.next(result);
    this.configSubject.next(null);
  }
}
